import type { RefObject } from 'react';
import { Archive } from 'lucide-react';
import { MetaFiltros } from '@/components/filtros/meta-filtros';
import type { ChipObra, ChipObraKey, VarianteConteoObras } from './obras-filtros-tipos';

// Meta-row de la home de Obras (R4a; síntesis del panel §2 «Meta-row y
// chips»): la `MetaFiltros` COMPARTIDA (conteo con la única región viva, los
// chips activos con «Limpiar todo» desde 2, y la regla de foco al quitarlos,
// todo eso vive en MetaFiltros) más el conteo, SIEMPRE con «Mostrando»
// (artboard `Home.dc.html:106,624`: la plantilla fija es «Mostrando N[ en
// curso] de M obras» — con el switch prendido se cae SOLO el «en curso», el
// «Mostrando» nunca se cae; hallazgo #1 reui-y-artboards, R4a) y la píldora de
// modo archivo — NEUTRA (decisión de la síntesis: naranja daba 2,9:1) y ya no
// removible (decisión 1 de Juan: el switch vive en la fila, no acá). La
// píldora va PEGADA al conteo, hermana inmediata del `<span aria-live>` (el
// artboard la dibuja como hermana inmediata del conteo, antes de los chips),
// pero AFUERA de la región viva: pasa por el slot `trasConteo` de
// `MetaFiltros` (decisión b del orquestador, hallazgo #2 home-comportamiento
// — con `aria-atomic`, la píldora adentro de la región viva se reanunciaba en
// cada tecleo del buscador, no solo al prender el switch) — a diferencia del
// «derecha» del Avivador (que empuja a la derecha del todo con «N pospuestas»
// / «Umbrales» / «?»): acá no va nada a la derecha (síntesis: «A la derecha no
// va nada»).

const fmt = (n: number) => n.toLocaleString('es-AR');

interface MetaObrasProps {
  n: number;
  total: number;
  variante: VarianteConteoObras;
  archivadas: boolean;
  /** Gate de escritura (mismo criterio que obra-card.tsx): la píldora solo le
   *  promete «cambiá el estado de redes desde cada card» a quien puede. */
  puedeEscribir: boolean;
  chips: readonly ChipObra[];
  onQuitarChip: (key: ChipObraKey) => void;
  onLimpiarTodo: () => void;
  focoSinChipsRef: RefObject<HTMLElement | null>;
}

function PildoraModoArchivo({ puedeEscribir }: { puedeEscribir: boolean }) {
  return (
    <span className="ml-2 inline-flex items-center gap-1.5 rounded-full border border-border-strong bg-surface-2 px-2.5 py-[3px] align-middle text-xs font-medium text-muted-foreground">
      <Archive className="h-3 w-3 flex-none" aria-hidden="true" />
      {puedeEscribir
        ? 'Modo archivo · cambiá el estado de redes desde cada card'
        : 'Modo archivo · incluye obras archivadas y No Aplica'}
    </span>
  );
}

export function MetaObras({
  n,
  total,
  variante,
  archivadas,
  puedeEscribir,
  chips,
  onQuitarChip,
  onLimpiarTodo,
  focoSinChipsRef,
}: MetaObrasProps) {
  return (
    <MetaFiltros
      conteo={
        <>
          Mostrando <b className="font-semibold tabular-nums text-foreground">{fmt(n)}</b>
          {variante === 'en-curso' && ' en curso'} de{' '}
          <b className="font-semibold tabular-nums text-foreground">{fmt(total)}</b> obras
        </>
      }
      trasConteo={archivadas && <PildoraModoArchivo puedeEscribir={puedeEscribir} />}
      chips={chips}
      onQuitarChip={onQuitarChip}
      onLimpiarTodo={onLimpiarTodo}
      focoSinChipsRef={focoSinChipsRef}
    />
  );
}
